import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { CountrySelector } from "./registration/CountrySelector";
import { NORDIC_COUNTRIES, type NordicCountry } from "./registration/constants";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import {
  validateInput,
  formatRegistrationNumber,
  getFormatHintLocalized,
  type ValidationResult,
} from "@/utils/registrationValidation";
import { AlertCircle } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { useLanguage } from "@/context/LanguageContext";
import { valuationLabels } from "@/i18n";
import { useNativePlatform } from "@/hooks/useNativePlatform";
import { cn } from "@/lib/utils";

interface RegistrationInputProps {
  onSubmit: (regNumber: string, isChassisSearch: boolean) => void;
}

export const RegistrationInput = ({ onSubmit }: RegistrationInputProps) => {
  const isMobile = useIsMobile();
  const { isNative } = useNativePlatform();
  const { language } = useLanguage();
  const vLabels = valuationLabels(language).registration;
  const [regNumber, setRegNumber] = useState("");
  const [selectedCountry, setSelectedCountry] = useState<NordicCountry>(
    NORDIC_COUNTRIES[0],
  );
  const [isChassisSearch, setIsChassisSearch] = useState(false);
  const [validation, setValidation] = useState<ValidationResult>({
    isValid: false,
  });
  const [isValidating, setIsValidating] = useState(false);

  const handleCountryChange = (countryId: string) => {
    const country = NORDIC_COUNTRIES.find((c) => c.id === countryId);
    if (country) {
      setSelectedCountry(country);
      // Re-validate when country changes
      if (regNumber) {
        validateCurrentInput();
      }
    }
  };

  const validateCurrentInput = () => {
    if (!regNumber.trim()) {
      setValidation({ isValid: false });
      return;
    }

    const result = validateInput(
      regNumber,
      selectedCountry.id,
      isChassisSearch,
      vLabels.validationError,
    );
    setValidation(result);
  };

  // Validate input when regNumber or search type changes
  useEffect(() => {
    if (regNumber) {
      setIsValidating(true);

      const timeoutId = setTimeout(() => {
        validateCurrentInput();
        setIsValidating(false);
      }, 300);

      return () => clearTimeout(timeoutId);
    } else {
      setValidation({ isValid: false });
    }
  }, [regNumber, selectedCountry.id, isChassisSearch]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    let value = e.target.value.toUpperCase();

    // Format Finnish registration numbers with dash
    if (!isChassisSearch && selectedCountry.id === "FI") {
      value = formatRegistrationNumber(value, selectedCountry.id);
    }

    setRegNumber(value);
  };

  const handleSubmit = () => {
    if (validation.isValid && regNumber.trim()) {
      onSubmit(regNumber.trim(), isChassisSearch);
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && validation.isValid && regNumber.trim()) {
      handleSubmit();
    }
  };

  const getMaxLength = () => {
    if (isChassisSearch) return 17;

    switch (selectedCountry.id) {
      case "SE":
        return 6;
      case "DK":
        return 7;
      case "FI":
        return 7; // ABC-123
      case "NO":
        return 7;
      default:
        return 7;
    }
  };

  const getPlaceholder = () => {
    if (isChassisSearch) return "CHASSINR";

    switch (selectedCountry.id) {
      case "SE":
        return "ABC123";
      case "DK":
        return "AB12345";
      case "FI":
        return "ABC-123";
      case "NO":
        return "AB12345";
      default:
        return "REG. NR.";
    }
  };

  return (
    <div
      className={cn(
        isNative && "native-reg-step-center",
      )}
    >
    <div className="space-y-3 sm:space-y-4 max-w-md mx-auto px-4 sm:px-0">
      <div className="space-y-2">
        <div className="flex items-start justify-between gap-2">
          <div className="flex flex-col gap-1 flex-1 min-w-0">
            <label className="text-xs text-muted-foreground">
              {isChassisSearch
                ? vLabels.chassisNumber
                : vLabels.registrationNumber}
            </label>
            <span className="text-xs text-gray-500 break-words">
              {getFormatHintLocalized(
                selectedCountry.id,
                isChassisSearch,
                vLabels,
              )}
            </span>
          </div>
          {/* TODO: Temporarily disabled chassis search because of AutoUncle API issues */}
          {/* <div className="flex items-center space-x-1.5 flex-shrink-0 pt-0.5">
            <Switch
              id="chassis-search"
              checked={isChassisSearch}
              onCheckedChange={setIsChassisSearch}
              className="scale-75"
            />
            <Label
              htmlFor="chassis-search"
              className="text-xs cursor-pointer whitespace-nowrap"
            >
              {isMobile ? "Chassinr" : "Sök med chassinr"}
            </Label>
          </div> */}
        </div>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-2 sm:gap-2">
          <div className="relative w-full max-w-[320px] sm:max-w-[380px]">
            <div className="absolute left-0 top-0 bottom-0 pl-2 sm:pl-3 pt-1 pr-1 sm:pr-2 w-[50px] sm:w-[60px] flex items-center justify-center bg-[#003399] rounded-l-md z-10">
              <CountrySelector
                selectedCountry={selectedCountry}
                onCountryChange={handleCountryChange}
                className="w-full h-full"
              />
            </div>
            <Input
              value={regNumber}
              onChange={handleInputChange}
              onKeyDown={handleKeyDown}
              placeholder={getPlaceholder()}
              className={`pl-[55px] sm:pl-[70px] text-center uppercase text-xl sm:text-[2.5rem] leading-[40px] sm:leading-[60px] tracking-[0.1em] sm:tracking-[0.2em] h-[40px] sm:h-[60px] w-full bg-white font-medium border-2 sm:border-[4px] rounded-lg text-[#4A5568] transition-colors ${
                validation.isValid && regNumber.trim()
                  ? "border-green-500"
                  : regNumber.trim() && !validation.isValid
                    ? "border-red-500"
                    : "border-[#003399]"
              }`}
              maxLength={getMaxLength()}
            />
            {isValidating && (
              <div className="absolute right-2 sm:right-3 top-1/2 transform -translate-y-1/2">
                <div className="animate-spin rounded-full h-3 w-3 sm:h-4 sm:w-4 border-b-2 border-[#003399]"></div>
              </div>
            )}
          </div>
          <Button
            onClick={handleSubmit}
            disabled={!validation.isValid || !regNumber.trim() || isValidating}
            size="sm"
            className="px-4 sm:px-6 h-[40px] sm:h-[60px] text-sm sm:text-base font-medium w-full sm:w-auto"
          >
            {vLabels.search}
          </Button>
        </div>

        {/* Validation error message */}
        {regNumber.trim() &&
          !validation.isValid &&
          !isValidating &&
          validation.error && (
            <div className="flex items-center gap-2 text-red-600 text-xs sm:text-sm mt-2">
              <AlertCircle className="h-3 w-3 sm:h-4 sm:w-4 flex-shrink-0" />
              <span>{vLabels.validationError}</span>
            </div>
          )}

        {/* Success message */}
        {validation.isValid && regNumber.trim() && (
          <div className="flex items-center gap-2 text-green-600 text-xs sm:text-sm mt-2">
            <span>{vLabels.correctFormat}</span>
          </div>
        )}
      </div>
    </div>
    </div>
  );
};
